import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import type { RootState } from '@/store'

interface User {
  id: string
  username: string
  fullName: string
  email: string
  phoneNumber?: string
  avatar?: string
  roles: string[]
  permissions: string[]
  /** True when the admin belongs to the SUPER_ADMIN group — FE must short-circuit all <Can> checks. */
  isSuperAdmin?: boolean
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  loading: boolean
  error: string | null
}

const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  loading: false,
  error: null,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload
      state.isAuthenticated = true
      state.error = null
    },
    setPermissions: (
      state,
      action: PayloadAction<{ permissions: string[]; isSuperAdmin: boolean }>,
    ) => {
      if (!state.user) return
      state.user.permissions = action.payload.permissions
      state.user.isSuperAdmin = action.payload.isSuperAdmin
    },
    setAccessToken: (state, action: PayloadAction<string>) => {
      state.accessToken = action.payload
    },
    setRefreshToken: (state, action: PayloadAction<string>) => {
      state.refreshToken = action.payload
    },
    clearUser: (state) => {
      state.user = null
      state.accessToken = null
      state.refreshToken = null
      state.isAuthenticated = false
      state.error = null
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
      state.loading = false
    },
    syncAuthState: (state) => {
      // Only clear Redux state if no token exists — no side effects
      if (!state.accessToken) {
        state.user = null
        state.refreshToken = null
        state.isAuthenticated = false
        state.error = null
      }
    },
    /**
     * Hardened version of `clearUser` — returns the entire slice to its initial
     * state. Use at the start of every login mutation (after the previous user
     * may have left a stale `permissions` / `isSuperAdmin` snapshot) so that
     * setUser + setPermissions can populate a clean slot.
     */
    resetAuthState: () => initialState,
  },
})

export const {
  setUser,
  setPermissions,
  setAccessToken,
  setRefreshToken,
  clearUser,
  setLoading,
  setError,
  syncAuthState,
  resetAuthState,
} = authSlice.actions

// Selectors
export const selectAuth = (state: RootState) => state.auth
export const selectUser = (state: RootState) => state.auth.user
export const selectAccessToken = (state: RootState) => state.auth.accessToken
export const selectIsAuthenticated = (state: RootState) =>
  state.auth.isAuthenticated
export const selectAuthLoading = (state: RootState) => state.auth.loading
export const selectAuthError = (state: RootState) => state.auth.error
export const selectPermissions = (state: RootState): string[] =>
  state.auth.user?.permissions ?? []
export const selectIsSuperAdmin = (state: RootState): boolean =>
  state.auth.user?.isSuperAdmin ?? false

export default authSlice.reducer
